// @file Dashboard navigation store
import { dashViewUrl } from '@@/bits/dash_helper'
import { __ } from '@@/bits/intl'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { OzTabsWithSeparatorButtonProps } from '@@/library/v4/components/OzTabsWithSeparator.vue'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashLayoutPickerStore } from '@@/pinia/dash_layout_picker_store'
import { MobilePage } from '@@/types/dash'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum DashTabs {
  HOME = 'Home',
  GALLERY = 'Gallery',
  JOIN = 'Join',
  MAKE = 'Make',
}

export const useDashNavigationStore = defineStore('dashNavigationStore', () => {
  const dashLayoutPickerStore = useDashLayoutPickerStore()
  const dashAccountsStore = useDashAccountsStore()

  // State
  const currentTabKey = ref<DashTabs>(DashTabs.HOME)

  // Getters
  const homePath = computed<string>(() => {
    return dashAccountsStore.isCurrentAccountLibrary ? dashAccountsStore.currentLibraryDashboardPath : '/dashboard'
  })
  const makePath = computed<string>(() => {
    return dashAccountsStore.isCurrentAccountLibrary
      ? `${dashAccountsStore.currentLibraryDashboardPath}/make`
      : '/dashboard/make'
  })
  const tabs = computed<OzTabsWithSeparatorButtonProps[]>(() => [
    {
      key: DashTabs.HOME,
      text: __('Home'),
      href: dashViewUrl({ mobilePage: MobilePage.AccountsMenu }, { path: homePath.value }),
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: DashTabs.GALLERY,
      text: __('Gallery'),
      href: '/dashboard/gallery/all',
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: DashTabs.JOIN,
      text: __('Join'),
    },
    {
      key: DashTabs.MAKE,
      text: __('+ Make'),
      href: dashViewUrl({ mobilePage: MobilePage.LayoutPicker }, { path: makePath.value }),
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
      disabled:
        dashLayoutPickerStore.cannotMakeWall &&
        dashAccountsStore.currentLibrary != null &&
        !dashAccountsStore.isCurrentUserLibraryOwner,
    },
  ])

  // Actions
  const switchTab = (tabKey: DashTabs): void => {
    currentTabKey.value = tabKey
  }

  return {
    // State
    currentTabKey,

    // Getters
    tabs,

    // Actions
    switchTab,
  }
})
